/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
  from,
  FetchResult,
  ApolloLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { getMainDefinition, Observable } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { useDispatch } from "react-redux";
import { TGlobalError, TUser } from "@/types/public";
import {
  setCredentials,
  setGlobalError,
} from "@/context/features/auth/authSlice";
import { setWsConnectionStatus } from "@/context/features/room/roomSlice";

const HTTP_URI =
  import.meta.env.VITE_GRAPHQL_HTTP || "http://localhost:7000/graphql";
const WS_URI =
  typeof window !== "undefined"
    ? `ws://${window.location.host}/graphql`
    : "ws://localhost:7000/graphql";
const MOTHER_SERVICE_URI =
  import.meta.env.VITE_BIOARC_SERVICE || "https://bioarc.ir";
const SKEW_MS = 30_000;

type TokenResp = {
  jwt: string;
  uid: string;
  hash: string;
  gid: string;
  shortCode: string;
  user: TUser;
  expiresAt?: number;
};

type CachedToken = {
  jwt: string;
  uid: string;
  gid: string;
  hash: string;
  shortCode: string;
  user: TUser;
  expiresAt: number | null;
  cachedAt: number;
};

async function getTokenFromService(
  meetId?: string,
  onErr?: (data: TGlobalError) => void
): Promise<TokenResp> {
  const res = await fetch(`${MOTHER_SERVICE_URI}/VisitOnline/Room/${meetId}`);
  const data = await res.json();
  if (!res.ok) {
    onErr?.({ errorCode: res.status, message: data?.message });
    return handleHttpError(res.status);
  }
  return {
    jwt: data.jwt,
    uid: data.uid,
    gid: data.gid,
    hash: data.hash,
    shortCode: data.shortCode,
    user: data.payload,
    expiresAt:
      typeof data?.payload?.exp === "number"
        ? data.payload.exp * 1000
        : undefined,
  };
}

function handleHttpError(status: number): never {
  console.error("HTTP status:", status);
  throw new Error(`HTTP error ${status}`);
}

function cacheKey(meetId: string) {
  return `authToken:${meetId}`;
}

function loadCachedToken(meetId?: string): CachedToken | null {
  if (!meetId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(meetId));
    if (!raw) return null;
    const data: CachedToken = JSON.parse(raw);
    if (!data?.jwt) return null;
    const now = Date.now();
    if (typeof data.expiresAt === "number" && data.expiresAt - SKEW_MS <= now) {
      window.localStorage.removeItem(cacheKey(meetId));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function saveCachedToken(meetId: string, v: CachedToken) {
  if (!meetId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(cacheKey(meetId), JSON.stringify(v));
  } catch (e) {
    console.error(e);
  }
}

function clearCachedToken(meetId?: string) {
  if (!meetId || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(cacheKey(meetId));
  } catch (e) {
    console.error(e);
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export default function GraphqlProvider({ children }: PropsWithChildren) {
  const [, meetId] =
    typeof window !== "undefined"
      ? window.location.pathname.split("/")
      : ["", ""];
  const tokenRef = useRef<string | null>(null);
  const expiryRef = useRef<number | null>(null);
  const inflightRef = useRef<Promise<string> | null>(null);
  const dispatch = useDispatch();
  // const DEFAULT_WS_PATH = "/graphql";

  const WS_URI = useMemo(() => {
    if (typeof window === "undefined") {
      return `ws://localhost:7000/graphql`;
    }

    if (import.meta.env.DEV) {
      return `ws://localhost:7000/graphql`;
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/graphql-ws`;
  }, []);

  useEffect(() => {
    if (!meetId) return;
    const cached = loadCachedToken(meetId);
    if (cached) {
      tokenRef.current = cached.jwt;
      expiryRef.current = cached.expiresAt ?? null;
      if (cached.shortCode && cached.uid && cached.user) {
        dispatch(
          setCredentials({
            user: cached.user,
            shortCode: cached.shortCode,
            uid: cached.uid,
            jwt: cached.jwt,
            gid: cached.gid,
            hash: cached.hash,
          })
        );
      }
    }
  }, [dispatch, meetId]);

  const ensureToken = useCallback(
    async (forceRefresh = false): Promise<string> => {
      const now = Date.now();
      const validInMemory =
        tokenRef.current &&
        (expiryRef.current == null || expiryRef.current - SKEW_MS > now);
      if (!forceRefresh && validInMemory) {
        return tokenRef.current!;
      }
      if (!meetId) throw new Error("Token unavailable");
      if (!forceRefresh) {
        const cached = loadCachedToken(meetId);
        if (cached) {
          tokenRef.current = cached.jwt;
          expiryRef.current =
            typeof cached.expiresAt === "number" ? cached.expiresAt : null;
          if (cached.shortCode && cached.uid && cached.user) {
            dispatch(
              setCredentials({
                user: cached.user,
                shortCode: cached.shortCode,
                uid: cached.uid,
                jwt: cached.jwt,
                gid: cached.gid,
                hash: cached.hash,
              })
            );
          }
          return cached.jwt;
        }
      }
      if (inflightRef.current) return inflightRef.current;
      inflightRef.current = (async () => {
        try {
          const { jwt, expiresAt, shortCode, uid, user, gid, hash } =
            await getTokenFromService(meetId, (data) => {
              dispatch(
                setGlobalError({
                  errorCode: data.errorCode,
                  message: data.message,
                })
              );
            });
          if (!jwt) {
            throw new Error("JWT missing");
          }
          tokenRef.current = jwt;
          const expMs = typeof expiresAt === "number" ? expiresAt : null;
          expiryRef.current = expMs;
          saveCachedToken(meetId, {
            jwt,
            uid,
            gid,
            hash,
            shortCode,
            user,
            expiresAt: expMs,
            cachedAt: Date.now(),
          });
          if (shortCode && uid && user) {
            dispatch(setCredentials({ user, shortCode, uid, jwt, gid, hash }));
          }
          return jwt;
        } catch (err) {
          clearCachedToken(meetId);
          throw err;
        } finally {
          inflightRef.current = null;
        }
      })();
      return inflightRef.current;
    },
    [dispatch, meetId]
  );

  useEffect(() => {
    let timer: number | undefined;
    const schedule = () => {
      if (!expiryRef.current) return;
      const delay = Math.max(1_000, expiryRef.current - Date.now() - SKEW_MS);
      timer = window.setTimeout(async () => {
        try {
          await ensureToken(true);
        } catch (e) {
          console.error(e);
        } finally {
          schedule();
        }
      }, delay);
    };
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [ensureToken]);

  const httpLink = useMemo(() => new HttpLink({ uri: HTTP_URI }), []);

  const authLink = useMemo(
    () =>
      setContext(async (_, { headers }) => {
        const token = await ensureToken(false);
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
          },
        };
      }),
    [ensureToken]
  );

  const errorLink = useMemo(
    () =>
      onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (networkError && "statusCode" in networkError) {
          handleHttpError((networkError as any).statusCode);
          return;
        }
        const unauth =
          graphQLErrors?.some(
            (e) =>
              e.extensions?.code === "UNAUTHENTICATED" ||
              e.message?.toLowerCase().includes("unauthenticated")
          ) ?? false;
        if (!unauth) return;
        return new Observable<FetchResult>((observer) => {
          (async () => {
            try {
              clearCachedToken(meetId);
              const newToken = await ensureToken(true);
              operation.setContext(({ headers = {} }) => ({
                headers: { ...headers, authorization: `Bearer ${newToken}` },
              }));
              const sub = forward(operation).subscribe({
                next: (value) => observer.next?.(value),
                error: (err) => observer.error?.(err),
                complete: () => observer.complete?.(),
              });
              return () => sub.unsubscribe();
            } catch (err) {
              observer.error?.(err);
            }
          })();
        });
      }),
    [ensureToken, meetId]
  );

  const setWsStatusDeferred = useCallback(
    (next: { connected: boolean; connecting: boolean }) => {
      setTimeout(() => dispatch(setWsConnectionStatus(next.connected)), 0);
    },
    [dispatch]
  );

  const pendingSubsRef = useRef<
    {
      op: any;
      forward: (op: any) => any;
      observer: any;
      cancelled: { v: boolean };
    }[]
  >([]);
  const wsReadyRef = useRef(false);
  const setWsReady = useCallback((ready: boolean) => {
    wsReadyRef.current = ready;
    if (ready && pendingSubsRef.current.length) {
      const queued = [...pendingSubsRef.current];
      pendingSubsRef.current = [];
      queued.forEach(({ op, forward, observer, cancelled }) => {
        if (cancelled.v) return;
        const sub = forward(op).subscribe({
          next: (v: any) => observer.next?.(v),
          error: (e: any) => observer.error?.(e),
          complete: () => observer.complete?.(),
        });
        observer.__sub = sub;
      });
    }
  }, []);

  const wsLink = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new GraphQLWsLink(
      createClient({
        url: WS_URI,
        lazy: false,
        retryAttempts: Infinity,
        retryWait: async (retries) => {
          const ms = Math.min(1000 * 2 ** retries, 10000);
          await sleep(ms);
        },
        connectionParams: async () => {
          const token = await ensureToken(false);
          const auth = token ? `Bearer ${token}` : "";
          // console.log({ auth });
          return {
            headers: {
              authorization: auth,
            },
          };
        },
        on: {
          connecting: () => {
            console.log("WebSocket connecting...");
            setWsStatusDeferred({ connected: false, connecting: true });
            setWsReady(false);
          },
          connected: () => {
            console.log("WebSocket connected");
            setWsStatusDeferred({ connected: true, connecting: false });
            setWsReady(true);
          },
          closed: async (event) => {
            setWsStatusDeferred({ connected: false, connecting: false });
            setWsReady(false);
            try {
              const code = (event as any)?.code;
              if (code === 4500 || code === 4401 || code === 4403) {
                await ensureToken(true);
              }
            } catch (err) {
              console.error("Error refreshing token after WS close:", err);
            }
          },
          error: () => {
            setWsStatusDeferred({ connected: false, connecting: false });
            setWsReady(false);
          },
        },
      })
    );
  }, [ensureToken, setWsStatusDeferred, setWsReady, WS_URI]);

  const tappedWsLink = useMemo(
    () =>
      wsLink &&
      new ApolloLink((op) => {
        // console.log("WS handling operation:", op.operationName);
        return wsLink.request(op);
      }),
    [wsLink]
  );

  const gateLink = useMemo(
    () =>
      new ApolloLink((op, forward) => {
        const def = getMainDefinition(op.query);
        const isSub =
          def.kind === "OperationDefinition" &&
          def.operation === "subscription";
        if (!isSub || wsReadyRef.current) {
          return forward!(op);
        }
        return new Observable((observer) => {
          const cancelled = { v: false };
          pendingSubsRef.current.push({
            op,
            forward: forward!,
            observer,
            cancelled,
          });
          return () => {
            cancelled.v = true;
          };
        });
      }),
    []
  );

  const link = useMemo(
    () =>
      from([
        errorLink,
        authLink,
        gateLink,
        split(
          ({ query }) => {
            const def = getMainDefinition(query);
            return (
              def.kind === "OperationDefinition" &&
              def.operation === "subscription"
            );
          },
          tappedWsLink ?? httpLink,
          httpLink
        ),
      ]),
    [errorLink, authLink, gateLink, tappedWsLink, httpLink]
  );

  const client = useMemo(
    () =>
      new ApolloClient({
        link,
        cache: new InMemoryCache(),
      }),
    [link]
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
